/**
 * IndianFamilyTreeFestivals — pan-India festival calendar for IFT.
 *
 * URL: /indian-family-tree/festivals
 *
 * Renders every seeded festival in `ift_festivals` with:
 *   • Month filter (Jan → Dec + Movable)
 *   • Tradition filter (Hindu, Muslim, Sikh, Christian, Secular)
 *   • Region filter (free text search)
 *   • Each card shows regions, family customs, and a link deeper.
 *
 * Historic aesthetic — mirrors the IFT palette (parchment + oxidised
 * gold + tulsi green) so it feels like a chapter of the archive.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Calendar, Flame, Loader2, MapPin, ScrollText, Search,
} from "lucide-react";
import { http } from "@/lib/api";
import SeoHead from "@/components/SeoHead";
import JsonLd, { breadcrumbSchema } from "@/components/JsonLd";

// Historic palette — matches IndianFamilyTreeHome.jsx.
const P = {
  bg:       "#F5EAD3",
  bgDeep:   "#E8D9B5",
  ink:      "#3A1F0F",
  inkSoft:  "#6B4A2E",
  saffron:  "#B25D38",
  gold:     "#C08A2E",
  goldSoft: "#EFDDB0",
  tulsi:    "#0E7C55",
  ivory:    "#FFF8E6",
};

const MONTHS = [
  "january","february","march","april","may","june",
  "july","august","september","october","november","december","movable",
];

const TRADITIONS = [
  { key: "",           label: "All traditions" },
  { key: "hindu",      label: "Hindu" },
  { key: "muslim",     label: "Muslim" },
  { key: "sikh",       label: "Sikh" },
  { key: "christian",  label: "Christian" },
  { key: "secular",    label: "National / Secular" },
];

const TRADITION_TINT = {
  hindu:     { bg: "bg-amber-50", ring: "ring-amber-200", text: "text-amber-900" },
  muslim:    { bg: "bg-emerald-50", ring: "ring-emerald-200", text: "text-emerald-900" },
  sikh:      { bg: "bg-orange-50", ring: "ring-orange-200", text: "text-orange-900" },
  christian: { bg: "bg-sky-50",   ring: "ring-sky-200",   text: "text-sky-900" },
  secular:   { bg: "bg-slate-50", ring: "ring-slate-200", text: "text-slate-900" },
};

export default function IndianFamilyTreeFestivals() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState("");
  const [tradition, setTradition] = useState("");
  const [region, setRegion] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const r = await http.get("/ift/festivals", {
          params: {
            month:     month || undefined,
            tradition: tradition || undefined,
            region:    region || undefined,
          },
        });
        if (!cancelled) setItems(r.data?.items || []);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [month, tradition, region]);

  const crumb = useMemo(() => ([
    { name: "IndianFamilyTree", url: "https://indianfamilytree.com/indian-family-tree" },
    { name: "Festivals",        url: "https://indianfamilytree.com/indian-family-tree/festivals" },
  ]), []);

  return (
    <div
      className="min-h-screen"
      style={{ background: P.bg, color: P.ink }}
      data-testid="ift-festivals"
    >
      <SeoHead
        section="indianfamilytree"
        title="Festival Calendar — Indian Family Tree"
        description="Pan-India festival calendar — every major Hindu, Muslim, Sikh, Christian and national festival with the family customs behind it."
      />
      <JsonLd data={breadcrumbSchema(crumb)} />

      {/* Header band */}
      <header
        className="border-b"
        style={{ background: P.bgDeep, borderColor: P.gold }}
      >
        <div className="mx-auto flex max-w-6xl flex-col gap-2 px-4 py-8 sm:py-12">
          <Link
            to="/indian-family-tree"
            data-testid="ift-festivals-back"
            className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-widest"
            style={{ color: P.tulsi }}
          >
            <ArrowLeft className="h-3 w-3" /> Indian Family Tree
          </Link>
          <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest" style={{ color: P.inkSoft }}>
            <Calendar className="h-3.5 w-3.5" /> Chapter · Festivals
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl" style={{ color: P.saffron }}>
            The year, mapped in festivals.
          </h1>
          <p className="max-w-3xl text-sm sm:text-base" style={{ color: P.inkSoft }}>
            Every major festival that shapes an Indian family&apos;s
            calendar — with the dates, the regions that observe it,
            and the household customs your grandparents can still recite.
            Curated as part of the IFT heritage archive.
          </p>
        </div>
      </header>

      {/* Filters */}
      <section
        className="border-b"
        style={{ borderColor: P.gold, background: P.ivory }}
        data-testid="ift-festivals-filters"
      >
        <div className="mx-auto flex max-w-6xl flex-col gap-3 px-4 py-4 sm:flex-row sm:items-end">
          <div className="flex-1">
            <label className="block text-[10px] font-mono uppercase tracking-widest" style={{ color: P.inkSoft }}>
              Month
            </label>
            <select
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              data-testid="ift-festivals-month"
              className="mt-1 w-full rounded-md border px-2 py-1.5 text-sm"
              style={{ borderColor: P.gold, background: P.bg, color: P.ink }}
            >
              <option value="">All months</option>
              {MONTHS.map((m) => (
                <option key={m} value={m}>{m[0].toUpperCase() + m.slice(1)}</option>
              ))}
            </select>
          </div>
          <div className="flex-1">
            <label className="block text-[10px] font-mono uppercase tracking-widest" style={{ color: P.inkSoft }}>
              Tradition
            </label>
            <select
              value={tradition}
              onChange={(e) => setTradition(e.target.value)}
              data-testid="ift-festivals-tradition"
              className="mt-1 w-full rounded-md border px-2 py-1.5 text-sm"
              style={{ borderColor: P.gold, background: P.bg, color: P.ink }}
            >
              {TRADITIONS.map((t) => (
                <option key={t.key} value={t.key}>{t.label}</option>
              ))}
            </select>
          </div>
          <div className="flex-1">
            <label className="block text-[10px] font-mono uppercase tracking-widest" style={{ color: P.inkSoft }}>
              Region
            </label>
            <div className="relative">
              <input
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                data-testid="ift-festivals-region"
                placeholder="e.g. Kerala, Punjab, Bengal"
                className="mt-1 w-full rounded-md border px-2 py-1.5 pr-8 text-sm"
                style={{ borderColor: P.gold, background: P.bg, color: P.ink }}
              />
              <Search className="absolute right-2 top-3.5 h-3.5 w-3.5" style={{ color: P.inkSoft }} />
            </div>
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="mx-auto max-w-6xl px-4 py-8">
        {loading ? (
          <div className="flex items-center gap-2 text-sm" style={{ color: P.inkSoft }}>
            <Loader2 className="h-4 w-4 animate-spin" /> Loading festivals…
          </div>
        ) : items.length === 0 ? (
          <div
            className="rounded-xl border border-dashed p-8 text-center text-sm"
            style={{ borderColor: P.gold, color: P.inkSoft }}
          >
            No festivals match these filters.
          </div>
        ) : (
          <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="ift-festivals-grid">
            {items.map((f) => {
              const tint = TRADITION_TINT[f.tradition] || TRADITION_TINT.secular;
              return (
                <li key={f.slug} data-testid={`ift-festival-card-${f.slug}`}>
                  <Link
                    to={`/indian-family-tree/festivals/${f.slug}`}
                    className={`group flex h-full flex-col gap-2 rounded-2xl border p-4 shadow-sm transition hover:shadow-md`}
                    style={{ borderColor: P.gold, background: P.ivory }}
                  >
                    <div className="flex items-center gap-2">
                      <Flame className="h-4 w-4" style={{ color: P.saffron }} />
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest ring-1 ${tint.bg} ${tint.ring} ${tint.text}`}
                      >
                        {f.tradition}
                      </span>
                      <span className="ml-auto text-[10px] font-mono uppercase tracking-widest" style={{ color: P.inkSoft }}>
                        {f.month}
                      </span>
                    </div>
                    <h3 className="font-serif text-lg leading-tight" style={{ color: P.ink }}>
                      {f.name}
                    </h3>
                    <div className="text-xs" style={{ color: P.inkSoft }}>
                      {f.typical_date}
                    </div>
                    {f.regions?.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {f.regions.slice(0, 3).map((r) => (
                          <span
                            key={r}
                            className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px]"
                            style={{ background: P.goldSoft, color: P.ink }}
                          >
                            <MapPin className="h-2.5 w-2.5" /> {r}
                          </span>
                        ))}
                      </div>
                    )}
                    <p className="line-clamp-3 text-sm" style={{ color: P.inkSoft }}>
                      {f.summary}
                    </p>
                    <div className="mt-auto pt-1 text-[11px] font-semibold" style={{ color: P.tulsi }}>
                      Read family customs →
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}

        <div className="mt-8 text-center text-[11px] font-mono uppercase tracking-widest" style={{ color: P.inkSoft }}>
          <ScrollText className="mx-auto mb-1 h-3.5 w-3.5" />
          Contribute a family custom → <Link to="/indian-family-tree" className="underline" style={{ color: P.tulsi }}>indianfamilytree.com</Link>
        </div>
      </section>
    </div>
  );
}
